/**
 * 이상한 방 - 파티 리빌의 이름표 ("OO이 찾았다" / "내가 찾았다" / "아무도 못 봤다", 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 5절(웃김 설계 포인트 6번 - 리빌은 클립 1순위 재료 · 3번 - 말싸움)
 *            `이상한방-상태모델-v0.1.md` 원칙 2(UI가 덮은 면적은 곧 볼 수 없는 방의 넓이)
 *            로드맵 M5(`revealParty` - 스텝마다 찾은 사람 이름표 또는 "아무도 못 봤다" 대형 스탬프)
 *
 * ## 리빌 라벨 아래의 두 줄
 *
 * 리빌은 스텝마다 "거실의 소파가 옮겨졌다"를 HUD 토스트로 띄운다(`sessionReveal.showLabel`). 여럿이서 그 문장에 빠진 것은
 * **누가**다 - "야 저거 내가 찾은 거잖아"·"그걸 아무도 못 봤어?"가 곧 파티 리빌의 대화라(5절), 그 한 줄을 토스트 바로 아래에
 * 작은 판으로 붙인다. M11에서 **누구 구획이었나**가 그 아래 한 줄로 더 붙는다 - 아무도 못 찾은 것은 곧 그 구획 주인의
 * 점수라(규칙 5), 이름이 없으면 "아무도 못 봤다"가 누구에게 좋은 일인지 화면에서 읽히지 않는다.
 * 문장에 이름을 섞지 않는 이유는 `reveal.RevealStep.foundBy` 주석에 있다(이름은 사람이지 문장이 아니다).
 *
 * ## 스텝이 곧 수명이다 - 타이머가 없다
 *
 * 이 판은 **다음 스텝이 갈아 끼울 때까지** 서 있다. 리빌 스텝은 2.5초(발견분 1초)이고 그동안 이름표가 사라지면 뒤늦게 화면을 본
 * 사람이 누가 찾았는지 놓친다. 그래서 `juice.ts`의 0.5초 규칙과 무관하다 - 그 규칙은 **수색 중** 연출의 것이고 리빌은 수색이
 * 아니다(관찰할 방이 더 없다). 걷는 것은 다음 스텝(`showStep`) · 리빌 종료(`clear`) · 무대 잠듦(`setVisible(false)`) 셋이고,
 * 정리에 `animationend`를 듣지 않는 것은 다른 화면과 같다(움직임 줄이기에서 안 온다).
 *
 * ## 대형 스탬프는 여기서 그리지 않는다
 *
 * "아무도 못 봤다" 스탬프는 `juice.nobodySaw`의 것이다 - 발견 연출의 언어(노란 증거 태그, 기울기 표)와 같은 손으로 찍혀야
 * 같은 화면의 물건으로 읽힌다. 이 파일은 그 얼굴이 `finale`일 때 그것을 **부르기만** 한다. 마지막 미발견에만 터지는 이유는
 * `matchSessionReveal.ts` 머리말에 있다.
 *
 * ## 자리
 *
 * HUD 토스트(`.gh-notice`, 위 58px) 바로 아래. 화면 중앙은 비운다(원칙 2) - 리빌 카메라가 그 한 건을 가운데에 놓기 때문이다.
 * `pointer-events: none`. 색은 전부 `--gh-*`.
 */

import { t } from '../i18n'
import type { JuiceHandle } from './juice'

import { createElement, restartAnimation, setHidden, setText } from './dom'
import { overlayLayout } from './overlayLayout'

import './revealParty.css'

/* --- 타입 ---------------------------------------------------------------------- */

/**
 * 이 이상한 곳의 **주인**(M11 - 그 구획을 다녀간 사람). 아무도 못 찾으면 그 사람의 점수가 되므로 이름이 함께 서야
 * "아무도 못 봤다"가 손해가 아니라 **누군가의 성적**으로 읽힌다. 좌석이 사라졌으면(퇴장) `null`이다.
 */
export type RevealPartyOwner = Readonly<{ name: string; isMe: boolean }> | null

/**
 * 스텝 하나의 얼굴. 만드는 것은 `game/matchSessionReveal.partyRevealFace`다 - 여기는 그리기만 한다.
 *  - `found`  남이 찾았다. 이름은 사전을 거치지 않는다(사람 이름은 번역 대상이 아니다).
 *  - `mine`   내가 찾았다.
 *  - `nobody` 아무도 못 봤다. `finale`이면 대형 스탬프(`juice.nobodySaw`)까지.
 *
 * `owner`는 셋 모두에 붙는다 - 누구 구획이었는지는 찾힌 것에도 웃음의 재료다("네 구획에서 그걸 못 숨겼냐").
 */
export type RevealPartyFace =
  | Readonly<{ kind: 'found'; name: string; owner: RevealPartyOwner }>
  | Readonly<{ kind: 'mine'; owner: RevealPartyOwner }>
  | Readonly<{ kind: 'nobody'; finale: boolean; owner: RevealPartyOwner }>

export type RevealPartyHandle = Readonly<{
  root: HTMLElement
  /** 스텝 진입. 앞의 판을 갈아 끼운다. `finale`이면 대형 스탬프도 함께. */
  showStep: (face: RevealPartyFace) => void
  /** 판을 걷는다. 리빌이 끝나거나 접힐 때. */
  clear: () => void
  /** 배치 모드·잠든 무대에서는 통째로 접는다(HUD의 `setVisible`과 같은 시점). 접히면 떠 있던 것도 걷는다. */
  setVisible: (visible: boolean) => void
  dispose: () => void
}>

export type RevealPartyOptions = Readonly<{
  mount: HTMLElement
  /** 대형 스탬프를 찍을 손. 여기서 스탬프를 따로 그리지 않는다(머리말). */
  juice: Pick<JuiceHandle, 'nobodySaw'>
}>

/* --- 문구 ---------------------------------------------------------------------- */

/** 얼굴 → 판의 글자. `found`만 이름을 넣고 나머지 둘은 사전 문장 그대로다. */
export function revealPartyText(face: RevealPartyFace): string {
  switch (face.kind) {
    case 'found':
      return t('reveal.party.foundBy', { name: face.name })
    case 'mine':
      return t('reveal.party.mine')
    case 'nobody':
      return t('reveal.party.nobody')
  }
}

/**
 * 얼굴 → 주인 줄(M11). 주인이 없으면(좌석이 사라졌다) `null`이고 그 줄은 서지 않는다.
 *
 * 아무도 못 찾은 것은 **점수의 말**로 적는다("내 점수가 된다" / "OO의 점수가 된다") - 그 순간 화면이 답해야 하는 것은
 * "누구 구획이었나"가 아니라 "이게 왜 좋은 일/나쁜 일인가"이고, 그것이 M11 규칙 5 그 자체다. 찾힌 것은 사실만 적는다
 * ("내 구획" / "OO의 구획") - 이미 남의 점수가 된 것에 점수의 말을 두 번 하면 문장이 길어지고 카메라가 가려진다.
 */
export function revealPartyOwnerText(face: RevealPartyFace): string | null {
  const owner = face.owner
  if (owner === null) return null
  if (face.kind === 'nobody') {
    return owner.isMe ? t('zone.reveal.nobodyMine') : t('zone.reveal.nobodyOwner', { name: owner.name })
  }
  return owner.isMe ? t('zone.reveal.ownerMine') : t('zone.reveal.owner', { name: owner.name })
}

/* --- 본체 ---------------------------------------------------------------------- */

export function createRevealParty(options: RevealPartyOptions): RevealPartyHandle {
  const root = createElement('div', 'rp-root')
  root.setAttribute('role', 'status')
  root.hidden = true
  /* CSS를 못 믿는 자리. 리빌 중에도 건너뛰기 버튼은 눌려야 한다. */
  root.style.pointerEvents = 'none'

  const plate = createElement('p', 'rp-plate', '')
  /** 주인 줄(M11). 판보다 작고 흐리다 - 누가 찾았는지가 먼저이고 누구 구획이었는지는 그 다음이다. */
  const ownerPlate = createElement('p', 'rp-owner', '')
  root.append(plate, ownerPlate)
  // 가운데 알림 열의 둘째 칸. 걸기 배너와 10px 차이로 겹치던 자리를 흐름으로 바꿨다(`overlayLayout.ts`).
  const layout = overlayLayout(options.mount)
  layout.place(root, 'reveal')

  let visible = true

  function clear(): void {
    root.className = 'rp-root'
    setHidden(root, true)
  }

  return {
    root,

    showStep(face: RevealPartyFace): void {
      // 잠든 무대에서는 찍지 않는다. 이름표는 시계를 잡지 않으므로 생략해도 게임이 멈추지 않는다.
      if (!visible) return
      setText(plate, revealPartyText(face))
      // 주인 줄. 없으면 접는다 - 빈 줄이 남으면 판의 높이가 스텝마다 흔들린다.
      const ownerText = revealPartyOwnerText(face)
      setText(ownerPlate, ownerText ?? '')
      setHidden(ownerPlate, ownerText === null)
      /* 얼굴이 곧 클래스다. 남의 이름표는 먹색 판, 내 것은 노란 판(내 발견 자막과 같은 색), 아무도 못 봤으면 경고색 글자. */
      root.className = `rp-root rp-root--${face.kind}`
      setHidden(root, false)
      /* 스텝마다 처음부터 돈다. 같은 얼굴이 연달아 오면(남 → 남) 클래스가 안 바뀌어 애니메이션이 이어져 보인다 - 그건 갈아 끼운 것이 아니다. */
      restartAnimation(plate)
      if (face.kind === 'nobody' && face.finale) options.juice.nobodySaw()
    },

    clear,

    setVisible(next: boolean): void {
      visible = next
      if (!next) clear()
    },

    dispose(): void {
      clear()
      // 기둥에서 걷는다 - `remove()`만 하면 관측기가 떼어 낸 노드를 계속 붙든다.
      layout.unplace(root)
    },
  }
}
